import { useCallback, useState } from 'react';
import type { StepDefinition, StepState, StepValidation } from '@/shared/app-stepper-form/types';

/**
 * Re-exported so existing importers keep working.
 *
 * `StepState` is declared in `types/` because the panel's props type needs it,
 * and a type module that imported from a hook would invert the dependency.
 */
export type { StepState } from '@/shared/app-stepper-form/types';

export type UseStepperOptions = {
  readonly steps: ReadonlyArray<StepDefinition>;
  readonly onSubmit: () => void | Promise<void>;
  readonly onStepChange?: (index: number, step: StepDefinition) => void;
};

/**
 * Re-validates every required step and reports the first that fails.
 *
 * Returns the index of the first invalid required step, or -1 when all pass.
 *
 * A user who edits step 1 after passing it must not be able to submit on the
 * strength of a stale pass. This is the check that stops a completed-looking
 * flow from hiding an invalid earlier step.
 */
function firstInvalidRequiredStep(
  steps: ReadonlyArray<StepDefinition>,
  record: (index: number, validation: StepValidation) => void,
): number {
  for (const [index, step] of steps.entries()) {
    if (step.required === false) continue;
    const validation = step.validate ? step.validate() : 'valid';
    record(index, validation);
    if (validation === 'invalid') return index;
  }
  return -1;
}

/**
 * Validation memory for every step, plus the recorder that updates it.
 *
 * Its own hook so the navigation logic below reads as navigation rather than as
 * a mixture of navigation and bookkeeping.
 */
function useStepStates(steps: ReadonlyArray<StepDefinition>) {
  const [states, setStates] = useState<ReadonlyArray<StepState>>(() =>
    steps.map(() => ({ validation: 'pending', blocked: false })),
  );

  const record = useCallback(
    (index: number, validation: StepValidation): void => {
      setStates((previous) =>
        previous.map((state, i) =>
          i === index
            ? {
                validation,
                // Matches the submit rule exactly: a step is required unless it
                // opts OUT with `required === false`. Testing `=== true` here
                // disagreed with submission, so a step that blocks submission
                // was not marked blocked in the rail.
                blocked: validation === 'invalid' && steps[i]?.required !== false,
              }
            : state,
        ),
      );
    },
    [steps],
  );

  return { states, record };
}

/**
 * The stepper state machine: position, validation memory and navigation.
 *
 * Extracted from the component so the component stays a composition of parts and
 * so the rules are testable without rendering. Note what is deliberately NOT
 * here: nothing about unsaved changes. That is the caller's knowledge, supplied
 * as `hasUnsavedChanges`, and the layout never decides it for itself.
 */
export function useStepper({ steps, onSubmit, onStepChange }: UseStepperOptions) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [furthestReached, setFurthestReached] = useState(0);
  const { states, record } = useStepStates(steps);

  const moveTo = useCallback(
    (index: number): void => {
      setActiveIndex(index);
      setFurthestReached((reached) => Math.max(reached, index));
      const step = steps[index];
      if (step) onStepChange?.(index, step);
    },
    [steps, onStepChange],
  );

  /**
   * Refuses to advance while the current step is invalid.
   *
   * Returns the resulting index, which equals the CURRENT index when blocked —
   * so a caller that ignores the return value still cannot advance.
   */
  const attemptAdvance = useCallback((): number => {
    const current = steps[activeIndex];
    if (!current) return activeIndex;

    const validation = current.validate ? current.validate() : 'valid';
    record(activeIndex, validation);

    if (validation === 'invalid' && current.required !== false) {
      return activeIndex;
    }

    const next = Math.min(activeIndex + 1, steps.length - 1);
    moveTo(next);
    return next;
  }, [steps, activeIndex, record, moveTo]);

  const attemptBack = useCallback((): void => {
    moveTo(Math.max(0, activeIndex - 1));
  }, [activeIndex, moveTo]);

  /**
   * Submission re-validates EVERY required step, not just the last.
   *
   * A user who edits step 1 after passing it must not be able to submit on the
   * strength of a stale pass. This is the check that stops a completed-looking
   * flow from hiding an invalid earlier step.
   */
  const submit = useCallback(async (): Promise<void> => {
    const blockedAt = firstInvalidRequiredStep(steps, record);
    if (blockedAt >= 0) {
      setActiveIndex(blockedAt);
      return;
    }
    await onSubmit();
  }, [steps, record, onSubmit]);

  /**
   * Whether the ACTIVE step is complete enough to leave.
   *
   * WHY THIS IS COMPUTED ON RENDER, WHEN `validate` IS OTHERWISE ONLY CALLED ON
   * ATTEMPT
   * -------------------------------------------------------------------------
   * The rule the stepper follows is that validation runs when the user TRIES to
   * move, never on every keystroke — flagging a half-typed slug before the user
   * has finished makes a form feel broken. That rule is about showing ERROR
   * TEXT, and this does not show any: it only decides whether the primary button
   * is enabled.
   *
   * The alternative — leaving Continue enabled and refusing the click — is worse
   * for exactly the user this is for. A button that looks live and does nothing
   * reads as a broken app, and the user has no way to tell which of the fields
   * above is the one holding them back. A disabled button with a visible
   * "Required" marker beside the offending field answers that question before it
   * is asked.
   *
   * Only the ACTIVE step is evaluated, so the cost is one predicate per render,
   * and the predicate is a pure function of the caller's own state.
   */
  const activeStep = steps[activeIndex];
  const canAdvance =
    activeStep === undefined ||
    activeStep.required === false ||
    (activeStep.validate ? activeStep.validate() !== 'invalid' : true);

  return {
    activeIndex,
    active: activeStep,
    states,
    furthestReached,
    isLast: activeIndex === steps.length - 1,
    canAdvance,
    attemptAdvance,
    attemptBack,
    jumpTo: moveTo,
    submit,
  };
}